/**
 * Value Formatters for Portfolio Table
 *
 * Provides consistent formatting for currency, numbers, percentages, and PnL values.
 * Extracted from DashboardDemo.tsx formatting patterns.
 */

/**
 * Format a currency value with locale-aware formatting.
 * Shows a '*' suffix for non-USD currencies instead of currency code.
 *
 * @param value - The numeric value to format
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @param showCurrency - Whether to show currency indicator (default: true)
 * @returns Formatted string like "1,234.56" or "1,234.56*" for non-USD
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string = "USD",
  showCurrency: boolean = true
): string {
  const numValue = Number(value) || 0;
  const formatted = numValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Add * suffix for non-USD currencies when showCurrency is true
  if (showCurrency && currency && currency !== "USD") {
    return `${formatted}*`;
  }
  return formatted;
}

/**
 * Format a currency value with USD prefix and $ symbol.
 * Returns "err" if value is null (e.g., FX rate unavailable).
 *
 * @param value - The numeric value to format
 * @returns Formatted string like "$1,234.56", or "err" if unavailable
 */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined) return "err";
  const numValue = Number(value) || 0;
  return `$${numValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Live USD columns whose nulls TableRow treats as unavailable, not formatUsd("err"). */
export const USD_COLUMN_IDS = [
  "mktValueUsd",
  "priceUsd",
  "unrealizedPnlUsd",
  "tradeValueUsd",
] as const;

/** Placeholder TableRow shows when a live USD field is null. */
export const USD_NULL_DISPLAY = "--";

export function isUsdColumn(columnId: string): boolean {
  return (USD_COLUMN_IDS as readonly string[]).includes(columnId);
}

export function isUsdValueUnavailable(value: unknown): boolean {
  return value === null || value === undefined;
}

/**
 * TableRow USD path: null/undefined is unavailable ("--"), not formatUsd("err").
 * formatUsd("err") remains the live-conversion failure contract when a value is required.
 */
export function formatPortfolioUsdDisplay(value: number | null | undefined): string {
  if (isUsdValueUnavailable(value)) return USD_NULL_DISPLAY;
  return formatUsd(value);
}

/**
 * IBKR account fields are stringly typed. Empty, blank, and non-finite values
 * are missing -- not zero.
 */
export function normalizeAccountNumber(
  value: number | string | null | undefined
): number | null {
  if (value === null || value === undefined) return null;
  const trimmed = typeof value === "string" ? value.trim() : value;
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * Account headline figures. Missing is N/A, not formatUsd("err") and not $0.00.
 */
export function formatAccountUsdOrNA(value: number | string | null | undefined): string {
  const n = normalizeAccountNumber(value);
  if (n === null) return "N/A";
  return formatUsd(n);
}

/**
 * Format a plain number with locale-aware thousand separators.
 *
 * @param value - The numeric value to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted string like "1,234"
 */
export function formatNumber(
  value: number | null | undefined,
  decimals: number = 0
): string {
  const numValue = Number(value) || 0;
  return numValue.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a percentage value.
 *
 * @param value - The numeric value (already as percentage, e.g., 12.5 for 12.5%)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string like "12.5%"
 */
export function formatPercent(
  value: number | null | undefined,
  decimals: number = 1
): string {
  const numValue = Number(value) || 0;
  return `${numValue.toFixed(decimals)}%`;
}

/**
 * Format a price value with specified decimal places.
 * Shows a '*' suffix for non-USD currencies instead of currency code.
 *
 * @param value - The numeric value to format
 * @param currency - Currency code to check
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string like "123.45" or "123.45*" for non-USD
 */
export function formatPrice(
  value: number | null | undefined,
  currency: string = "USD",
  decimals: number = 2
): string {
  const numValue = Number(value) || 0;
  const formatted = numValue.toFixed(decimals);
  // Add * suffix for non-USD currencies
  return currency && currency !== "USD" ? `${formatted}*` : formatted;
}

/**
 * Format a PnL (Profit and Loss) value with sign indicator.
 *
 * @param value - The numeric value to format
 * @param currency - Currency code to prefix
 * @returns Formatted string like "+USD 1,234.56" or "-USD 500.00"
 */
export function formatPnl(
  value: number | null | undefined,
  currency: string = "USD"
): string {
  const numValue = Number(value) || 0;
  const sign = numValue >= 0 ? "+" : "";
  const absValue = Math.abs(numValue).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}${currency} ${absValue}`;
}

/**
 * Get the CSS class for PnL coloring based on value.
 *
 * @param value - The numeric value to check
 * @returns CSS class string for text color
 */
export function getPnlColorClass(value: number | null | undefined): string {
  const numValue = Number(value) || 0;
  if (numValue > 0) return "text-green-400";
  if (numValue < 0) return "text-red-400";
  return "";
}

/**
 * Get the CSS class for position/shares coloring based on value.
 *
 * @param value - The numeric value to check
 * @returns CSS class string for text color
 */
export function getPositionColorClass(value: number | null | undefined): string {
  const numValue = Number(value) || 0;
  return numValue >= 0 ? "text-green-400" : "text-red-400";
}

/**
 * Format a trade quantity with sign indicator.
 *
 * @param value - The numeric value to format
 * @returns Formatted string like "+100" or "-50"
 */
export function formatTradeQty(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const numValue = Number(value) || 0;
  if (numValue === 0) return "0";
  const sign = numValue > 0 ? "+" : "";
  return `${sign}${numValue.toLocaleString()}`;
}

/**
 * Format an FX rate value.
 * Shows "err" if rate is null (fetch failed).
 *
 * @param value - The FX rate value
 * @param decimals - Optional fixed decimal places (default: auto-detect based on magnitude)
 * @returns Formatted string with appropriate decimal places, or "err" if unavailable
 */
export function formatFxRate(value: number | null | undefined, decimals?: number): string {
  if (value === null || value === undefined) return "err";
  const numValue = Number(value) || 0;
  // If decimals provided, use it; otherwise use more decimal places for small rates (like JPY)
  const decimalPlaces = decimals ?? (numValue < 0.1 ? 4 : 2);
  return numValue.toFixed(decimalPlaces);
}

/**
 * Format a nullable string value, showing a dash for null/empty.
 *
 * @param value - The string value to format
 * @returns The value or "-" if null/empty
 */
export function formatNullable(value: string | null | undefined): string {
  return value || "-";
}

